import {LOCAL_STORAGE_MAIN_BRANCH_ID} from "./constants/LocalStorage";

const BRANCH_PARAM = "branch_id"
const FROM_PARAM = "from"

/** Values of the `from` URL param. */
const THIS_APP = "legacy"
const V2_APP = "v2"

/** sessionStorage key: which app sent this tab here (loop guard). */
const ORIGIN_KEY = "_DISPATCH_HANDOFF_ORIGIN_"

/** Basic iiko id check; real access is checked in ApplicationDataProvider. */
const ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,63}$/

interface Arrival {
    from: string
    branchId: string
}

const readArrival = (): Arrival | null => {
    try {
        const raw = sessionStorage.getItem(ORIGIN_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        if (!parsed || typeof parsed !== "object") return null
        const {from, branchId} = parsed as Partial<Arrival>
        if (typeof from !== "string" || typeof branchId !== "string") return null
        return {from, branchId}
    } catch {
        // Storage unavailable or corrupted: skip the loop guard.
        return null
    }
}

const rememberArrival = (arrival: Arrival) => {
    try {
        sessionStorage.setItem(ORIGIN_KEY, JSON.stringify(arrival))
    } catch {
        /* storage unavailable: skip the loop guard */
    }
}

/** Saves the branch from v2 and cleans the URL so a reload won't replay it. */
export const readIncomingBranch = () => {
    const url = new URL(window.location.href)
    const branchId = url.searchParams.get(BRANCH_PARAM) ?? ""
    const from = url.searchParams.get(FROM_PARAM) ?? ""

    if (!branchId && !from) return

    url.searchParams.delete(BRANCH_PARAM)
    url.searchParams.delete(FROM_PARAM)
    window.history.replaceState(null, "", url.toString())

    if (!ID_PATTERN.test(branchId)) return

    rememberArrival({from, branchId})
    // JSON-encoded to match useLocalStorage.
    localStorage.setItem(LOCAL_STORAGE_MAIN_BRANCH_ID, JSON.stringify(branchId))
}

/** Feature flag for redirecting to v2; off unless exactly "true". */
export const isHandoffToV2Enabled = (): boolean =>
    process.env.REACT_APP_HANDOFF_TO_V2_ENABLED === "true"

/** True if v2 just sent this branch here, so redirecting back would loop. */
export const arrivedFromV2 = (branchId: string): boolean => {
    const arrival = readArrival()
    return arrival?.from === V2_APP && arrival.branchId === branchId
}

/** Clears the loop guard so the branch can be handed to v2 again. */
export const forgetArrival = () => {
    try {
        sessionStorage.removeItem(ORIGIN_KEY)
    } catch {
        /* storage unavailable: skip the loop guard */
    }
}

/** v2 URL for the branch, or null if unconfigured; base is env-only to prevent open redirects. */
export const dispatchV2UrlFor = (branchId: string): string | null => {
    const base = process.env.REACT_APP_DISPATCH_V2_URL
    if (!base || !branchId) return null

    let url: URL
    try {
        url = new URL(base)
    } catch {
        return null
    }
    if (url.protocol !== "https:" && url.protocol !== "http:") return null

    url.searchParams.set(BRANCH_PARAM, branchId)
    url.searchParams.set(FROM_PARAM, THIS_APP)
    return url.toString()
}

/** Opens v2 without a Back entry; returns false if the v2 URL isn't configured. */
export const redirectToDispatchV2 = (branchId: string): boolean => {
    const url = dispatchV2UrlFor(branchId)
    if (!url) return false

    window.location.replace(url)
    return true
}
